import { AnyObject } from "final-form";
import { get } from "lodash";
import React from "react";
import { FieldProps, FieldRenderProps } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { connect } from "react-redux";
import { Col, Container, Row } from "reactstrap";
import Icon, { IconName } from "src/components/Icon/Icon";
import CheckboxField from "../../../../components/Form/CheckboxFieldV2/CheckboxFieldV2";
import SimpleDatePickerField from "../../../../components/Form/SimpleDatePickerFieldV2/SimpleDatePickerField";
import SingleSelectField from "../../../../components/Form/SingleSelectFieldV2/SingleSelectField";
import TextField from "../../../../components/Form/TextFieldV2/TextFieldV2";
import * as S from "../../../../constants/StringConstants";
import { defaultCondition, defaultMedication } from "../../../../store/models/client.model";
import { IRootState } from "../../../../store/reducers";

interface IMedicalNotesProps<FieldValue, el extends HTMLElement>
  extends FieldProps<FieldValue, FieldRenderProps<FieldValue, el>> {
  formValues: AnyObject;
}

export default class MedicalNotes<el extends HTMLElement> extends React.Component<
  IMedicalNotesProps<any, HTMLElement>
> {
  render() {
    const { name, formValues } = this.props;

    return (
      <FieldArray name={`${name}`}>
        {({ fields }) => (
          <Container fluid className="bootsrap-container-override">
            <Row>
              <Col>
                {fields.map((name, index) => (
                  <MedicalCondition
                    key={name}
                    name={name}
                    formValues={formValues}
                    remove={fields.remove}
                    index={index}
                  />
                ))}
              </Col>
            </Row>
            <Row>
              <Col className="mb-5">
                <button
                  className="button-v2 button-v2__secondary-mini"
                  onClick={e => {
                    e.preventDefault();
                    fields.push(defaultCondition);
                  }}>
                  {S.MN_ADD_CONDITION_LABEL}
                </button>
              </Col>
            </Row>
          </Container>
        )}
      </FieldArray>
    );
  }
}

interface IMedicalConditionProps<FieldValue, el extends HTMLElement>
  extends FieldProps<FieldValue, FieldRenderProps<FieldValue, el>> {
  formValues: AnyObject;
  index: number;
  remove: (index: number) => any;
}

class MedicalCondition<el extends HTMLElement> extends React.Component<IMedicalConditionProps<any, el>> {
  render() {
    const { name, formValues, index, remove } = this.props;

    const hasTakenMedications = get(formValues, `${name}.hasTakenMedications`);

    return (
      <Container fluid className="bootsrap-container-override">
        <Row>
          <Container>
            <Row>
              <Col sm="3">
                <TextField
                  key="condition-name-field"
                  name={`${name}.conditionName`}
                  label={S.MN_CONDITION_NAME_LABEL}
                />
              </Col>

              <Col sm="3">
                <SimpleDatePickerField
                  key="date-diagnosis-field"
                  name={`${name}.diagnosisDate`}
                  label={S.MN_DATE_OF_DIAGNOSIS_LABEL}
                  isFormMode
                />
              </Col>
              <Col sm="3">
                <TextField
                  key="treatment-type-field"
                  name={`${name}.treatmentType`}
                  label={S.MN_TYPE_OF_TREATMENT_LABEL}
                />
              </Col>
              <Col sm="3">
                <CheckboxField
                  key={"taken-medications-field"}
                  name={`${name}.hasTakenMedications`}
                  label={S.MN_MEDICATIONS_TAKEN_LABEL}
                  labelPosition="above"
                />
              </Col>
              <Row className="justify-content-end">
                <Col xs="auto">
                  <button className="button-v2__medication-link-blue large" onClick={() => remove(index)}>
                    <span className="remove-contact-button__label">
                      <Icon name={IconName.RemoveMinusCircleBlue} size="20px" />
                      {S.PBC_DELETE}
                    </span>
                  </button>
                </Col>
              </Row>
            </Row>

            {hasTakenMedications && (
              <Row>
                <Col sm="12">
                  <MedicationsComponent key={"medications-list"} name={`${name}.medications`} />
                </Col>
                <Col sm="12">
                  <TextField
                    key="additional-notes-field"
                    name={`${name}.additionalNotes`}
                    label={S.MN_ADDITIONAL_NOTES_LABEL}
                  />
                </Col>
              </Row>
            )}
          </Container>
        </Row>
      </Container>
    );
  }
}

interface IMedicationsProps<FieldValue, el extends HTMLElement>
  extends MedicationsStateProps,
    MedicationsDispatchProps,
    FieldProps<FieldValue, FieldRenderProps<FieldValue, el>> {}

class Medications<el extends HTMLElement> extends React.Component<IMedicationsProps<any, el>> {
  render() {
    const { name, frequencyOptions } = this.props;

    return (
      <FieldArray name={`${name}`}>
        {({ fields }) => (
          <Container className="bootsrap-container-override">
            {fields.map((name, index) => (
              <Row key={index}>
                <Col>
                  <TextField
                    key={`${index}-medication-name-field`}
                    name={`${name}.name`}
                    label={S.MN_MEDICATION_NAME_LABEL}
                  />
                </Col>
                <Col>
                  <TextField key={`${index}-medication-dose-field`} name={`${name}.dose`} label={S.MN_DOSE_LABEL} />
                </Col>
                <Col>
                  <SingleSelectField
                    key={`${index}-medication-frequency-field`}
                    name={`${name}.frequencyTypeID`}
                    label={S.MN_FREQUENCY_LABEL}
                    options={frequencyOptions}
                  />
                </Col>
              </Row>
            ))}
            <Row>
              <Col>
                <button
                  className="button__medication-link"
                  key="add-medication-button"
                  onClick={e => {
                    e.preventDefault();
                    fields.push(defaultMedication);
                  }}>
                  {S.MN_ADD_MEDICATION_BUTTON}
                </button>
              </Col>
            </Row>
          </Container>
        )}
      </FieldArray>
    );
  }
}

const mapStateToProps = (state: IRootState) => ({
  frequencyOptions: state.proposalOptions.clientOptions.selectMedicationFrequencyList,
  user: state.auth.user,
});

const mapDispatchToProps = {};

type MedicationsStateProps = ReturnType<typeof mapStateToProps>;
type MedicationsDispatchProps = typeof mapDispatchToProps;

const MedicationsComponent = connect(mapStateToProps, mapDispatchToProps)(Medications);
